<script>
import { mapActions, mapGetters } from 'vuex'
import { Salutation } from '@/enums/Salutation'
import { jwtGetClaim } from '@/helpers/jwt.js'
import { fieldValidationsMixin } from '@/mixins/fieldValidationsMixin'
import { validationMixin } from 'vuelidate'
import { required, minLength, sameAs } from 'vuelidate/lib/validators'
import InvitationDetails from './InvitationDetails.vue'

export default {
  components: {
    InvitationDetails
  },
  mixins: [validationMixin, fieldValidationsMixin],
  validations: {
    user: {
      salutation: { required },
      firstname: { required },
      lastname: { required },
      password: {
        required,
        minLength: minLength(6),
        hasCapital(pwd) {
          return /[A-Z]/.test(pwd)
        },
        hasNum(pwd) {
          return /[0-9]/.test(pwd)
        },
        hasSpecialChar(pwd) {
          return /\W|_/.test(pwd)
        }
      },
      passwordRepeat: {
        required,
        minLength: minLength(6),
        sameAs: sameAs(function () {
          return this.user.password
        })
      }
    }
  },
  props: {
    token: {
      type: String,
      default: ''
    },
    firstname: {
      type: String,
      default: ''
    },
    lastname: {
      type: String,
      default: ''
    },
    tokenDecoded: {
      type: Object
    }
  },
  data: vm => ({
    loading: false,
    salutations: [
      {
        value: Salutation.Sir,
        text: vm.$t('form.label.mr')
      },
      {
        value: Salutation.Madam,
        text: vm.$t('form.label.mrs')
      }
    ],
    user: {
      salutation: 0,
      firstname: vm.$props.firstname || '',
      lastname: vm.$props.lastname || '',
      password: '',
      passwordRepeat: ''
    },
    showPassword: false,
    showPasswordRepeat: false,
    invitationStatus: 'error', // initial state
    dialog: {
      state: false,
      error: {
        color: 'error',
        title: vm.$t('global.save.error'),
        message: vm.$t('global.save.error'),
        button: vm.$t('button.ok'),
        relocation: ''
      },
      success: {
        color: 'success',
        title: vm.$t(
          'component.profile.administrationManagement.invite.accepted'
        ),
        message: vm.$t(
          'component.profile.administrationManagement.invite.accepted',
          {
            '{{OrganizationName}}': vm.tokenDecoded.OrganizationName
          }
        ),
        button: vm.$t('button.ok'),
        relocation: 'dashboard'
      }
    }
  }),
  computed: {
    ...mapGetters(['locale'])
  },
  methods: {
    ...mapActions([
      'setUserData',
      'setPermissions',
      'getUserProfile',
      'getUserSettings',
      'getCreditBalance',
      'loginUser'
    ]),
    async accept() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.loading = true
        await this.$organizationService
          .approveInvitation({
            data: {
              salutation: this.user.salutation,
              firstName: this.user.firstname,
              lastName: this.user.lastname,
              password: this.user.password
            },
            token: this.token
          })
          .then(response => {
            if (response.status === 200) {
              this.invitationStatus = 'success'
              this.prepareLogin({
                user: {
                  username: jwtGetClaim(response.jwt, 'emailaddress')
                },
                response: { jwt: response.jwt }
              })
            } else this.invitationStatus = 'error'
          })
          .catch(() => {
            this.invitationStatus = 'error'
          })
          .finally(() => {
            this.loading = false
            this.dialog.state = true
          })
      }
    },
    async prepareLogin(userData) {
      this.loading = true
      await this.loginUser({
        username: userData.user.username,
        password: this.user.password
      })
      await this.setUserData(userData)
      await this.setPermissions(userData.response.jwt)
      await this.getUserProfile()
      await this.getUserSettings()
      if (this.$permissions.hasCreditAccount) {
        await this.getCreditBalance()
      }
      this.loading = false
    },
    relocate(location) {
      if (location) {
        this.$router.push({
          name: location || 'login',
          params: { locale: this.locale }
        })
      } else {
        this.dialog.state = false
      }
    }
  }
}
</script>
<template>
  <div>
    <v-dialog v-model="dialog.state" max-width="500" persistent>
      <v-card>
        <DialogBaseColoredTitle :color="dialog[invitationStatus].color">{{
          dialog[invitationStatus].title
        }}</DialogBaseColoredTitle>
        <v-divider></v-divider>
        <DialogBaseText>
          <span v-html="dialog[invitationStatus].message" />
        </DialogBaseText>
        <v-divider></v-divider>
        <DialogBaseActions>
          <v-btn
            elevation="4"
            :color="dialog[invitationStatus].color"
            class="white--text ml-0 px-8 py-2"
            @click="relocate(dialog[invitationStatus].relocation)"
            >{{ dialog[invitationStatus].button }}</v-btn
          >
        </DialogBaseActions>
      </v-card>
    </v-dialog>
    <v-card width="500">
      <DialogBaseText>
        <InvitationDetails :token-decoded="tokenDecoded" />
      </DialogBaseText>
      <DialogBaseText>
        <v-divider></v-divider>
        <v-row>
          <v-col
            >{{ $t('component.profile.administrationManagement.insert.data') }}
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col cols="12">
            <v-select
              id="invitation_salutation"
              v-model="user.salutation"
              :items="salutations"
              item-value="value"
              item-text="text"
              :label="$t('form.label.salutation')"
              :error-messages="[...fieldNeccessary($v.user.salutation, $v)]"
              :hint="
                $t('component.profile.administrationManagement.required.field')
              "
              persistent-hint
              outlined
              dense
              required
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              id="invitation_firstname"
              v-model="user.firstname"
              :label="$t('form.label.first.name') + '*'"
              :error-messages="[...fieldNeccessary($v.user.firstname, $v)]"
              :hint="
                $t('component.profile.administrationManagement.required.field')
              "
              persistent-hint
              outlined
              dense
              required
              @input="$v.user.firstname.$touch()"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              id="invitation_lastname"
              v-model="user.lastname"
              :label="$t('form.label.last.name') + '*'"
              :error-messages="[...fieldNeccessary($v.user.lastname, $v)]"
              :hint="
                $t('component.profile.administrationManagement.required.field')
              "
              persistent-hint
              outlined
              dense
              required
              @input="$v.user.lastname.$touch()"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              id="invitation_password"
              v-model="user.password"
              :label="$t('form.label.password') + '*'"
              :error-messages="[
                ...fieldNeccessary($v.user.password, $v),
                ...password($v.user.password, $v)
              ]"
              :type="showPassword ? 'text' : 'password'"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :hint="
                $t('component.profile.administrationManagement.required.field')
              "
              persistent-hint
              counter
              outlined
              dense
              required
              @input="$v.user.password.$touch()"
              @keyup.enter.native="submit()"
              @click:append="showPassword = !showPassword"
            ></v-text-field>
          </v-col>
          <v-col>
            <v-text-field
              id="invitation_password_repeat"
              v-model="user.passwordRepeat"
              :label="$t('form.label.password.repeat') + '*'"
              :error-messages="[
                ...fieldNeccessary($v.user.passwordRepeat, $v),
                ...passwordRepeat($v.user.passwordRepeat, $v)
              ]"
              :type="showPasswordRepeat ? 'text' : 'password'"
              :append-icon="showPasswordRepeat ? 'mdi-eye' : 'mdi-eye-off'"
              :hint="
                $t('component.profile.administrationManagement.required.field')
              "
              persistent-hint
              counter
              outlined
              dense
              required
              @input="$v.user.passwordRepeat.$touch()"
              @keyup.enter.native="submit()"
              @click:append="showPasswordRepeat = !showPasswordRepeat"
            ></v-text-field>
          </v-col>
        </v-row>
      </DialogBaseText>
      <v-divider></v-divider>
      <DialogBaseActions>
        <v-btn
          :loading="loading"
          color="primary"
          class="white--text px-8 py-2"
          @click="accept"
          ><span>{{ $t('button.approve') }}</span></v-btn
        >
      </DialogBaseActions>
    </v-card>
  </div>
</template>
